import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import {
  ChevronDown,
  Crosshair,
  Crown,
  Mail,
  Megaphone,
  MessageCircle,
  Play,
  Plus,
  RefreshCw,
  ScrollText,
  Settings,
  Shield,
  Shirt,
  Store,
  Ticket,
  Trophy,
  UserMinus,
  UserRound,
  Users,
  WifiOff,
  X,
} from 'lucide-preact';
import { MODE_INFO } from '@vm/content';
import type { ModeId } from '@vm/shared';
import { audio } from '../../game/audio/Audio';
import { haptic } from '../platform';
import { CharacterPreview } from '../three/CharacterPreview';
import { onlineModes } from '../state/content';
import { go, openModal } from '../state/nav';
import { cancelQueue, isPartyMember, quickPlay } from '../state/play';
import { prefs } from '../state/prefs';
import { retryOnline } from '../state/session';
import { kickFromParty, leaveParty, openChat, setPartyReady } from '../state/socialActions';
import {
  battlePass,
  catalog,
  chatUnread,
  claimableAchievements,
  claimableQuests,
  claimableTiers,
  conn,
  cosmetics,
  friends,
  isOffline,
  loadout,
  me,
  onlineCount,
  onlineFriends,
  party,
  queue,
  quests,
} from '../state/store';
import { portalChatDisabled } from '../crazygames';
import { settings } from '../settings';
import { fmtClock, fmtNum } from '../ui/format';
import { useBusy, useNow } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { Avatar, Badge, Bar, Countdown, CurrencyPill, Emblem, LevelBadge, Modal } from '../ui/kit';
import { PortalNotice } from '../ui/PortalNotice';
import { openMyProfile, openProfile } from './Profile';
import { openPartyInvite } from './Friends';

// Top bar -----------------------------------------------------------------------------------------------------

function ProfileChip() {
  const m = me.value;
  if (!m) return null;
  const p = m.progression;
  return (
    <button class="lb-profile" onClick={openMyProfile} aria-label="My profile">
      <LevelBadge level={p.level} size="lg" />
      <div class="lb-profile-info">
        <div class="lb-profile-name">
          {m.clan && <span class="lb-clan-tag">[{m.clan.tag}]</span>}
          <span>{m.name}</span>
        </div>
        <Bar value={p.xpInLevel} max={p.xpToNext} tone="xp" class="lb-xp" label={`${fmtNum(p.xpInLevel)} / ${fmtNum(p.xpToNext)} XP`} />
      </div>
    </button>
  );
}

function OfflineBadge() {
  const [busy, run] = useBusy();
  return (
    <button class="lb-offline" disabled={busy} onClick={() => run(retryOnline)} title="Retry connection">
      <WifiOff size={18} />
      <span>Offline</span>
      <RefreshCw size={16} class={busy ? 'ui-spin' : ''} />
    </button>
  );
}

function TopBar() {
  const unread = me.value?.unreadInbox ?? 0;
  return (
    <header class="lb-top">
      <ProfileChip />
      <div class="lb-top-right">
        {isOffline.value && <OfflineBadge />}
        <ChatButton where="top" />
        <CurrencyPill kind="coins" />
        <CurrencyPill kind="gems" plus />
        <button class="lb-icon-btn" aria-label="Inbox" disabled={isOffline.value} onClick={() => go('inbox')}>
          <Mail size={24} />
          <Badge count={unread} />
        </button>
        <button class="lb-icon-btn" aria-label="Settings" onClick={() => go('settings')}>
          <Settings size={24} />
        </button>
      </div>
    </header>
  );
}

// Left navigation -----------------------------------------------------------------------------------------------

function NavButton(props: { label: string; icon: ComponentChildren; tone: string; onClick: () => void; badge?: number; sub?: string }) {
  return (
    <button class={`lb-nav-btn tone-${props.tone}`} onClick={props.onClick}>
      <span class="lb-nav-icon">
        {props.icon}
        <Badge count={props.badge ?? 0} />
      </span>
      <span class="lb-nav-label">{props.label}</span>
      {props.sub && <span class="lb-nav-sub">{props.sub}</span>}
    </button>
  );
}

function LeftNav() {
  const incoming = friends.value?.incoming.length ?? 0;
  const clan = me.value?.clan;
  const online = onlineFriends.value;
  return (
    <nav class="lb-nav">
      <NavButton label="Armory" tone="orange" icon={<Crosshair size={30} strokeWidth={2.5} />} onClick={() => go('armory')} />
      <NavButton label="Wardrobe" tone="pink" icon={<Shirt size={30} strokeWidth={2.5} />} onClick={() => go('wardrobe')} />
      <NavButton label="Clan" tone="blue" icon={clan ? <Emblem emblem={clan.emblem} size={34} /> : <Shield size={30} strokeWidth={2.5} />} onClick={() => go('clan')} />
      <NavButton label="Friends" tone="green" icon={<Users size={30} strokeWidth={2.5} />} badge={incoming} sub={online ? `${online} online` : undefined} onClick={() => go('friends')} />
      <NavButton label="Ranks" tone="yellow" icon={<Trophy size={30} strokeWidth={2.5} />} onClick={() => go('leaderboards')} />
    </nav>
  );
}

// Stage (character + party) ---------------------------------------------------------------------------------------

function openMemberMenu(userId: string, name: string, amLeader: boolean): void {
  openModal((close) => (
    <Modal title={name} size="sm" onClose={close}>
      <div class="menu-list">
        <button
          class="menu-item"
          onClick={() => {
            close();
            openProfile(userId, name);
          }}
        >
          <UserRound size={20} /> View profile
        </button>
        {amLeader && (
          <button
            class="menu-item danger"
            onClick={() => {
              close();
              void kickFromParty(userId);
            }}
          >
            <UserMinus size={20} /> Remove from party
          </button>
        )}
      </div>
    </Modal>
  ));
}

function PartyStrip() {
  const p = party.value;
  const m = me.value;
  const offline = isOffline.value;
  if (offline || !m) return null;
  if (!p || p.members.length <= 1) {
    return (
      <div class="lb-party">
        <button class="lb-party-invite" onClick={openPartyInvite}>
          <Plus size={20} strokeWidth={3} />
          <span>Invite friends</span>
        </button>
      </div>
    );
  }
  const amLeader = p.leaderId === m.id;
  const mine = p.members.find((x) => x.id === m.id);
  return (
    <div class="lb-party is-active">
      {p.members.map((x) => (
        <button
          key={x.id}
          class={`lb-party-member ${x.ready || x.id === p.leaderId ? 'is-ready' : ''}`}
          title={x.name}
          onClick={() => (x.id === m.id ? openMyProfile() : openMemberMenu(x.id, x.name, amLeader))}
        >
          <Avatar name={x.name} skin={x.id === m.id ? cosmetics.value.skin : undefined} armor={cosmetics.value.armor} size={40} />
          {x.id === p.leaderId && (
            <span class="lb-party-crown">
              <Crown size={14} strokeWidth={3} />
            </span>
          )}
          <span class="lb-party-name">{x.name}</span>
        </button>
      ))}
      <button class="lb-party-add" onClick={openPartyInvite} aria-label="Invite to party">
        <Plus size={20} strokeWidth={3} />
      </button>
      {!amLeader && mine && (
        <button class={`lb-party-ready ${mine.ready ? 'is-on' : ''}`} onClick={() => setPartyReady(!mine.ready)}>
          {mine.ready ? 'Ready!' : 'Ready?'}
        </button>
      )}
      <button class="lb-party-leave" onClick={() => leaveParty()} aria-label="Leave party" title="Leave party">
        <X size={18} strokeWidth={3} />
      </button>
    </div>
  );
}

function Stage() {
  const c = cosmetics.value;
  const m = me.value;
  const primary = loadout.value.weapons.find((w) => !!w) ?? 'brick_rifle';
  const pet = loadout.value.pet;
  return (
    <section class="lb-stage">
      <div class="lb-stage-floor" aria-hidden="true" />
      <div class="lb-char">
        <CharacterPreview skin={c.skin} armor={c.armor} weapon={primary} camo={c.camos?.[primary]} scope={c.scopes?.[primary]} pet={pet || undefined} petStyle={c.petStyle} rotate className="lb-char-preview" />
      </div>
      {m && (
        <div class="lb-nameplate">
          {m.clan && <Emblem emblem={m.clan.emblem} size={26} />}
          <span>{m.name}</span>
        </div>
      )}
      <PartyStrip />
    </section>
  );
}

// Right column ------------------------------------------------------------------------------------------------------

function EventBanner() {
  const c = catalog.value;
  const now = useNow(30000);
  if (!c) return null;
  const ev = c.events.find((e) => Date.parse(e.startsAt) <= now && Date.parse(e.endsAt) > now);
  if (ev) {
    return (
      <div class="lb-event">
        <Megaphone size={22} class="lb-event-icon" />
        <div class="lb-event-text">
          <b>{ev.name}</b>
          <span>{ev.description}</span>
        </div>
        <Countdown to={ev.endsAt} class="lb-event-timer" />
      </div>
    );
  }
  if (!c.motd) return null;
  return (
    <div class="lb-event motd">
      <Megaphone size={22} class="lb-event-icon" />
      <div class="lb-event-text">
        <span>{c.motd}</span>
      </div>
    </div>
  );
}

function SideCard(props: { tone: string; icon: ComponentChildren; title: string; sub?: ComponentChildren; badge?: number; onClick: () => void; children?: ComponentChildren; disabled?: boolean }) {
  return (
    <button class={`lb-card tone-${props.tone}`} onClick={props.onClick} disabled={props.disabled}>
      <span class="lb-card-icon">{props.icon}</span>
      <span class="lb-card-main">
        <span class="lb-card-title">{props.title}</span>
        {props.sub && <span class="lb-card-sub">{props.sub}</span>}
        {props.children}
      </span>
      <Badge count={props.badge ?? 0} />
    </button>
  );
}

function PassCard() {
  const bp = battlePass.value;
  const tier = bp?.tier ?? 0;
  const per = bp?.season.xpPerTier ?? 1;
  const inTier = bp ? (tier >= bp.season.tiers ? per : bp.xp - tier * per) : 0;
  return (
    <SideCard tone="purple" icon={<Ticket size={26} />} title="Battle Pass" badge={claimableTiers.value} onClick={() => go('battlepass')} disabled={isOffline.value}>
      {bp ? (
        <>
          <span class="lb-card-sub">
            Tier {tier} / {bp.season.tiers}
            {bp.premium && <span class="lb-premium">Premium</span>}
          </span>
          <Bar value={Math.max(0, inTier)} max={per} tone="purple" class="lb-card-bar" />
        </>
      ) : (
        <span class="lb-card-sub">{isOffline.value ? 'Offline' : 'Season rewards'}</span>
      )}
    </SideCard>
  );
}

function QuestCard() {
  const q = quests.value;
  const daily = q?.daily ?? [];
  const done = daily.filter((x) => x.completed).length;
  return (
    <SideCard tone="green" icon={<ScrollText size={26} />} title="Quests" badge={claimableQuests.value + claimableAchievements.value} onClick={() => go('quests')} disabled={isOffline.value}>
      {q ? (
        <>
          <span class="lb-card-sub">
            {done} / {daily.length} daily done
          </span>
          <Bar value={done} max={Math.max(1, daily.length)} tone="green" class="lb-card-bar" />
        </>
      ) : (
        <span class="lb-card-sub">{isOffline.value ? 'Offline' : 'Daily & weekly'}</span>
      )}
    </SideCard>
  );
}

function StoreCard() {
  const c = catalog.value;
  const offer = c?.offers.find((o) => o.featured) ?? c?.offers[0];
  return (
    <SideCard tone="orange" icon={<Store size={26} />} title="Store" onClick={() => go('store')} disabled={isOffline.value}>
      <span class="lb-card-sub">{offer ? <Countdown to={offer.endsAt} prefix={`${offer.name} · `} /> : isOffline.value ? 'Offline' : 'Gems & looks'}</span>
    </SideCard>
  );
}

function currentMode(): ModeId {
  const modes = onlineModes.value;
  const p = party.value;
  // Party members follow the leader's pick.
  const want = p && p.leaderId !== me.value?.id && p.mode ? p.mode : prefs.value.mode;
  if (modes.some((m) => m.id === want)) return want;
  return modes[0]?.id ?? 'tdm';
}

function PlayArea() {
  const q = queue.value;
  const now = useNow(q.state === 'searching' ? 500 : 60000);
  const offline = isOffline.value;
  const mode = currentMode();
  const info = MODE_INFO[mode];
  const member = isPartyMember();
  const myId = me.value?.id;
  const mine = party.value?.members.find((x) => x.id === myId);
  const [pressed, setPressed] = useState(false);
  let button: ComponentChildren;
  if (q.state === 'searching') {
    button = (
      <div class="lb-play is-searching">
        <div class="lb-play-main">
          <span class="lb-play-label">Searching…</span>
          <span class="lb-play-sub">
            {MODE_INFO[q.mode]?.name ?? q.mode} · {fmtClock((now - q.since) / 1000)}
            {q.estimate > 0 && ` (~${fmtClock(q.estimate)})`}
          </span>
        </div>
        <button class="lb-play-cancel" onClick={() => cancelQueue()} aria-label="Cancel matchmaking">
          <X size={26} strokeWidth={3.5} />
        </button>
      </div>
    );
  } else if (member && mine) {
    button = (
      <button class={`lb-play ${mine.ready ? 'is-ready' : ''}`} onClick={() => setPartyReady(!mine.ready)}>
        <div class="lb-play-main">
          <span class="lb-play-label">{mine.ready ? 'READY!' : 'READY UP'}</span>
          <span class="lb-play-sub">Your party leader starts the match</span>
        </div>
      </button>
    );
  } else {
    button = (
      <button
        class={`lb-play ${pressed ? 'is-pressed' : ''}`}
        onClick={() => {
          audio.unlock();
          audio.play('ui_click');
          haptic(18);
          setPressed(true);
          setTimeout(() => setPressed(false), 250);
          if (offline) go('play', { tab: 'practice' });
          else void quickPlay(mode, prefs.value.maps[mode]);
        }}
      >
        <Play size={40} fill="currentColor" strokeWidth={0} class="lb-play-icon" />
        <div class="lb-play-main">
          <span class="lb-play-label">{offline ? 'PRACTICE' : 'PLAY'}</span>
          <span class="lb-play-sub">{offline ? 'Offline vs bots' : info?.name}</span>
        </div>
      </button>
    );
  }
  return (
    <div class="lb-playarea">
      <button class="lb-mode" onClick={() => go('play', offline ? { tab: 'practice' } : {})}>
        <span class="lb-mode-icon">
          <Icon name={info?.icon ?? 'swords'} size={22} />
        </span>
        <span class="lb-mode-text">
          <small>{offline ? 'Offline' : 'Mode'}</small>
          <b>{offline ? 'Practice & Sandbox' : info?.name}</b>
        </span>
        <ChevronDown size={22} strokeWidth={3} />
      </button>
      {button}
      {/* Like the website: a handful online would undersell the game, so small counts stay hidden. */}
      {onlineCount.value !== null && onlineCount.value >= 5 && conn.value === 'online' && <div class="lb-online-count">{fmtNum(onlineCount.value)} marines online</div>}
    </div>
  );
}

function SideColumn() {
  return (
    <aside class="lb-side">
      <EventBanner />
      <div class="lb-cards">
        <PassCard />
        <QuestCard />
        <StoreCard />
      </div>
      <PlayArea />
    </aside>
  );
}

function ChatButton(props: { where: 'corner' | 'top' }) {
  const unread = Object.values(chatUnread.value).reduce((a, b) => a + b, 0);
  if (!settings.value.chatEnabled || portalChatDisabled.value || isOffline.value) return null;
  return (
    <button class={`lb-chat-btn in-${props.where}`} onClick={() => openChat()} aria-label="Open chat">
      <MessageCircle size={26} />
      <span>Chat</span>
      <Badge count={unread} />
    </button>
  );
}

export function Lobby() {
  return (
    <div class="lobby">
      <TopBar />
      <LeftNav />
      <Stage />
      <SideColumn />
      <ChatButton where="corner" />
      <PortalNotice />
    </div>
  );
}
